// UnifiedInvoiceActionDialog.jsx
// Renders instantly from props. Roster/payouts fetched lazily in background.
// No loading gate on the form — user can start filling immediately.

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, Button, TextField, Typography, Box, Alert,
  CircularProgress, IconButton, Paper, Chip, Checkbox,
  FormControlLabel, Divider, Grid, Radio, RadioGroup,
  Collapse, useMediaQuery, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, Stack,
} from '@mui/material';
import {
  Check as CheckIcon, Close as CloseIcon, Upload as UploadIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  Security as SecurityIcon, Gavel as GavelIcon, Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Person as PersonIcon, People as PeopleIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SignatureCanvas from 'react-signature-canvas';
import client from '../../../API';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

// ─────────────────────────────────────────────────────────────────────────── //
const UnifiedInvoiceActionDialog = ({
  open,
  onClose,
  invoiceId,
  onSuccess,
  unifiedInvoiceActionMutation,
  showNavigation = false,
  currentItemIndex = 1,
  totalItems = 1,
  onNavigateNext = null,
  onNavigatePrevious = null,
  onBackToList = null,
  currentItemName = '',
  invoiceAmount = 0,
  invoiceType = 'client',
  requiresApprovalProof = false,
  isFirstInvoiceForPayor = false,
  siblingInvoiceIds = [],
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── Action state ─────────────────────────────────────────────────────── //
  const [action, setAction]           = useState('approve');
  const [approveWord, setApproveWord] = useState('');
  const [reasoning, setReasoning]     = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── AP / PO fields ───────────────────────────────────────────────────── //
  const [apEmail, setApEmail]               = useState('');
  const [poRef, setPoRef]                   = useState('');
  const [erp, setErp]                       = useState('');
  const [externalPoNumber, setExternalPoNumber] = useState('');
  const [authorityType, setAuthorityType]   = useState('leader');
  const [agreeTerms, setAgreeTerms]         = useState(false);
  const [agreeAuthority, setAgreeAuthority] = useState(false);

  // ── Proof ────────────────────────────────────────────────────────────── //
  const [proofType, setProofType]       = useState('upload');
  const [file, setFile]                 = useState(null);
  const [filePreview, setFilePreview]   = useState(null);
  const sigCanvasRef  = useRef(null);
  const fileInputRef  = useRef(null);

  // ── Background-fetched roster ────────────────────────────────────────── //
  const [payoutsData, setPayoutsData]         = useState([]);
  const [hasExistingNoa, setHasExistingNoa]   = useState(false);
  const [noaData, setNoaData]                 = useState(null);
  const [apInfo, setApInfo]                   = useState(null);

  // ── UI expand state ──────────────────────────────────────────────────── //
  const [apSectionOpen, setApSectionOpen]   = useState(true);
  const [justifyOpen, setJustifyOpen]       = useState(false);
  const [showRoster, setShowRoster]         = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  // ── Effects ──────────────────────────────────────────────────────────── //
  useEffect(() => {
    if (!open || !invoiceId) return;
    resetForm();
    setJustifyOpen(requiresApprovalProof);

    let cancelled = false;
    const fetchRoster = async () => {
      try {
        const data = invoiceType === 'creator'
          ? await client.creatorConnect.getInvoice(invoiceId)
          : await client.invoices.fetch(invoiceId);
        if (cancelled) return;

        const payouts = data?.payouts || data?.creator_payments || [];
        setPayoutsData(payouts);

        if (data?.po_number || data?.invoice?.po_number)
          setPoRef(data.invoice?.po_number || data.po_number);

        if (data?.ap_info) {
          setApInfo(data.ap_info);
          if (data.ap_info.ap_email)           setApEmail(data.ap_info.ap_email);
          if (data.ap_info.vendor_system_name) setErp(data.ap_info.vendor_system_name);
        }
        if (data?.noa_data) {
          setNoaData(data.noa_data);
          setHasExistingNoa(true);
          if (data.noa_data.noa_sent_to && !apEmail) setApEmail(data.noa_data.noa_sent_to);
          setApSectionOpen(false);
        }
        if (data?.ap_info?.ap_info_verified) setApSectionOpen(false);
      } catch {
        // silent — form is already usable
      }
    };
    fetchRoster();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId]);

  if (!open) return null;

  // ── Derived ──────────────────────────────────────────────────────────── //
  const isApproveValid =
    action !== 'approve' ||
    (approveWord === 'Approve' &&
      emailRegex.test(apEmail.trim()) &&
      !!poRef.trim() &&
      agreeTerms &&
      agreeAuthority &&
      (!requiresApprovalProof || (proofType === 'upload' ? !!file : !sigCanvasRef.current?.isEmpty())));

  const hasZeroAmount = payoutsData.some((p) => parseFloat(p.amount ?? p.net_amount ?? 0) === 0);
  const showAmounts   = payoutsData.length > 0 && !hasZeroAmount;
  const creatorCount  = payoutsData.length;

  // ── Handlers ─────────────────────────────────────────────────────────── //
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    if (!f.type.startsWith('image/') && f.type !== 'application/pdf') { alert('Image or PDF only'); return; }
    setFile(f);
    setFilePreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };

  const buildProof = async () => {
    const proof = {
      reasoning: reasoning || undefined,
      date_confirmed: new Date().toISOString(),
      approval_method: 'digital_approval_system',
      approver_confirmation: approveWord,
      ap_email: apEmail.trim(),
      po_ref: poRef.trim(),
      erp_system: erp.trim() || undefined,
      external_po_number: externalPoNumber.trim() || undefined,
      authority_type: authorityType,
      invoice_amount: invoiceAmount,
      invoice_type: invoiceType,
      has_existing_noa: hasExistingNoa,
      is_first_invoice_for_payor: !!isFirstInvoiceForPayor,
    };
    if (proofType === 'upload' && file) {
      await new Promise((res, rej) => {
        const r = new FileReader();
        r.readAsDataURL(file);
        r.onload  = () => { proof.screenshot_data = r.result; proof.screenshot_filename = file.name; proof.proof_type = 'document_upload'; res(); };
        r.onerror = () => rej(new Error('Read failed'));
      });
    }
    if (proofType === 'signature' && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      proof.signature_data = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');
      proof.proof_type     = 'digital_signature';
    }
    return proof;
  };

  const handleSubmit = async () => {
    if (!invoiceId || !isApproveValid) return;
    setIsLoading(true);
    setErrorMessage('');
    try {
      const payload = { invoiceId, action };
      if (action === 'approve') payload.approval_proof = await buildProof();
      await unifiedInvoiceActionMutation(payload);

      if (action === 'approve' && siblingInvoiceIds?.length) {
        const sibProof = { ...payload.approval_proof, is_sibling_auto_approval: true, primary_invoice_id: invoiceId };
        await Promise.allSettled(
          siblingInvoiceIds.map((id) =>
            unifiedInvoiceActionMutation({ invoiceId: id, action: 'approve', approval_proof: { ...sibProof } })
              .catch((e) => console.warn(`Sibling ${id} failed:`, e))
          )
        );
      }

      const msg = action === 'approve'
        ? `Invoice approved!${siblingInvoiceIds?.length ? ` All ${siblingInvoiceIds.length + 1} batch invoices processed.` : ''}`
        : 'Invoice declined.';
      onSuccess?.(msg);
      if (!showNavigation) onClose?.();
    } catch (err) {
      setErrorMessage(err?.response?.data?.error || err?.message || 'Unexpected error.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setAction('approve'); setApproveWord(''); setReasoning('');
    setFile(null); setFilePreview(null);
    setApEmail(''); setPoRef(''); setErp(''); setExternalPoNumber('');
    setAuthorityType('leader'); setAgreeTerms(false); setAgreeAuthority(false);
    setProofType('upload'); setApInfo(null); setNoaData(null);
    setHasExistingNoa(false); setApSectionOpen(true); setJustifyOpen(false);
    setPayoutsData([]); setShowRoster(false); setMobileDetailOpen(false);
    sigCanvasRef.current?.clear();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { resetForm(); onClose?.(); };

  // ── Roster section ───────────────────────────────────────────────────── //
  const RosterSection = () => {
    if (payoutsData.length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Box
          onClick={() => setShowRoster((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {creatorCount > 1 ? <PeopleIcon fontSize="small" color="primary" /> : <PersonIcon fontSize="small" color="primary" />}
            {creatorCount > 1 ? `${creatorCount} Recipients` : 'Recipient'}
          </Typography>
          {showRoster ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
        <Collapse in={showRoster}>
          <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
            <List dense disablePadding>
              {payoutsData.map((p, i) => {
                const name = p.creator_name || p.display_name || p.creator_id || `Creator ${i + 1}`;
                const amt  = p.net_amount ?? p.amount;
                return (
                  <React.Fragment key={p.id || i}>
                    {i > 0 && <Divider component="li" />}
                    <ListItem sx={{ px: 2, py: 0.75 }}>
                      <ListItemAvatar sx={{ minWidth: 34 }}>
                        <Avatar src={p.creator_pfp} sx={{ width: 26, height: 26, fontSize: '0.7rem' }}>
                          {name[0]?.toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography variant="body2" fontWeight={600}>{name}</Typography>}
                        secondary={p.creator_id && p.creator_id !== name ? `@${p.creator_id}` : undefined}
                      />
                      {showAmounts && amt != null && (
                        <Typography variant="body2" fontWeight={700} color="success.main">{fmt(amt)}</Typography>
                      )}
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
            {showAmounts && creatorCount > 1 && (
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">Total</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {fmt(payoutsData.reduce((s, p) => s + Number(p.net_amount ?? p.amount ?? 0), 0))}
                </Typography>
              </Box>
            )}
          </Paper>
        </Collapse>
        {!showRoster && (
          <Typography variant="caption" color="text.secondary">
            {showAmounts
              ? `${creatorCount} contractor${creatorCount !== 1 ? 's' : ''} · ${fmt(payoutsData.reduce((s, p) => s + Number(p.net_amount ?? p.amount ?? 0), 0))} total`
              : `${creatorCount} contractor${creatorCount !== 1 ? 's' : ''}`}
          </Typography>
        )}
        {siblingInvoiceIds?.length > 0 && (
          <Alert severity="info" sx={{ mt: 1 }} icon={false}>
            <Typography variant="caption">
              Approving covers all <strong>{siblingInvoiceIds.length + 1} batch invoices</strong>.
            </Typography>
          </Alert>
        )}
      </Box>
    );
  };

  // ── Approval form ────────────────────────────────────────────────────── //
  const ApprovalForm = ({ compact = false }) => (
    <>
      {/* Step 2 — confirm word */}
      <Paper sx={{ p: compact ? 2 : 3, mb: compact ? 2 : 3, borderRadius: 2 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={700} gutterBottom>
          2. Confirm Authorization
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2">
            Type <strong>"Approve"</strong> to confirm authorization of {fmt(invoiceAmount)}
          </Typography>
        </Alert>
        <TextField
          label="Type 'Approve' to confirm"
          value={approveWord}
          onChange={(e) => setApproveWord(e.target.value)}
          fullWidth size="small"
          error={approveWord !== '' && approveWord !== 'Approve'}
          helperText={
            approveWord === 'Approve' ? '✓ Confirmed'
            : approveWord ? 'Must type exactly "Approve"'
            : 'Required'
          }
        />
      </Paper>

      {/* Step 3 — AP info */}
      <Paper sx={{ p: 0, mb: compact ? 2 : 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          onClick={() => setApSectionOpen((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: compact ? 2 : 3, cursor: 'pointer', bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}
        >
          <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={700}>3. AP Information</Typography>
          {apSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={apSectionOpen}>
          <Box sx={{ px: compact ? 2 : 3, pb: compact ? 2 : 3, pt: 1 }}>
            {hasExistingNoa && (
              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>✓ NOA on File</Typography>
                {noaData?.noa_sent_to && <Typography variant="caption">Sent to: {noaData.noa_sent_to}</Typography>}
              </Alert>
            )}
            {apInfo?.ap_info_verified && !hasExistingNoa && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>AP info pre-verified from company profile</Typography>
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                  required label="AP Department Email" value={apEmail}
                  onChange={(e) => setApEmail(e.target.value)}
                  fullWidth size="small"
                  error={apEmail !== '' && !emailRegex.test(apEmail)}
                  helperText={apEmail && !emailRegex.test(apEmail) ? 'Invalid email' : hasExistingNoa ? 'NOA already sent here' : 'NOA will be sent here'}
                />
              </Grid>
              <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                  required label="Blitz PO / Invoice Number" value={poRef}
                  onChange={(e) => setPoRef(e.target.value)}
                  fullWidth size="small" helperText="Blitz invoice reference"
                />
              </Grid>
              <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                  label="ERP System" value={erp}
                  onChange={(e) => setErp(e.target.value)}
                  fullWidth size="small" placeholder="Workday, SAP, Oracle…" helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                  label={erp ? `${erp} PO Number` : 'External PO Number'}
                  value={externalPoNumber}
                  onChange={(e) => setExternalPoNumber(e.target.value)}
                  fullWidth size="small" helperText="Optional — for reconciliation"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>Authority Type</Typography>
                <RadioGroup row={!compact} value={authorityType} onChange={(e) => setAuthorityType(e.target.value)}>
                  <FormControlLabel value="leader"   control={<Radio size="small" />} label={<Typography variant="body2">Team Leader</Typography>} />
                  <FormControlLabel value="delegate" control={<Radio size="small" />} label={<Typography variant="body2">Delegated Approver</Typography>} />
                  <FormControlLabel value="finance"  control={<Radio size="small" />} label={<Typography variant="body2">Finance Department</Typography>} />
                </RadioGroup>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Step 4 — docs (optional, collapsed by default unless requiresApprovalProof) */}
      <Paper sx={{ p: 0, mb: compact ? 2 : 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          onClick={() => setJustifyOpen((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: compact ? 2 : 3, cursor: 'pointer', bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={700}>
              4. Documentation
            </Typography>
            {requiresApprovalProof
              ? <Chip label="Required" color="error" size="small" sx={{ height: 20 }} />
              : <Typography variant="caption" color="text.secondary">(Optional)</Typography>}
          </Box>
          {justifyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={justifyOpen}>
          <Box sx={{ px: compact ? 2 : 3, pb: compact ? 2 : 3, pt: 1 }}>
            <TextField
              multiline rows={3} value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              fullWidth size="small"
              placeholder="Business reason for this approval…"
              helperText="Adds to audit trail"
              sx={{ mb: 2 }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} gutterBottom>Supporting Documentation</Typography>
            <RadioGroup row value={proofType} onChange={(e) => setProofType(e.target.value)} sx={{ mb: 2 }}>
              <FormControlLabel value="upload"    control={<Radio size="small" />} label="Upload Document" />
              <FormControlLabel value="signature" control={<Radio size="small" />} label="Digital Signature" />
            </RadioGroup>

            {proofType === 'upload' && (
              <Box sx={{ textAlign: 'center', p: 3, border: '2px dashed', borderColor: 'grey.300', borderRadius: 2, bgcolor: 'grey.50' }}>
                {!file ? (
                  <>
                    <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Email approval, signed doc, or authorization evidence · Max 5MB
                    </Typography>
                    <Button variant="contained" component="label" size="small">
                      Choose File
                      <input ref={fileInputRef} type="file" hidden accept="image/*,application/pdf" onChange={handleFileChange} />
                    </Button>
                  </>
                ) : (
                  <Box>
                    {filePreview && <img src={filePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, marginBottom: 8 }} />}
                    {!filePreview && <Box sx={{ p: 2, bgcolor: 'grey.200', borderRadius: 2, mb: 1 }}><Typography>📄 {file.name}</Typography></Box>}
                    <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(1)} KB</Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <Button variant="outlined" color="error" size="small" onClick={() => { setFile(null); setFilePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                        Remove
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {proofType === 'signature' && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Draw your signature</Typography>
                <Box sx={{ border: '2px solid', borderColor: 'grey.300', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                  <SignatureCanvas
                    ref={sigCanvasRef} penColor="black"
                    canvasProps={{ width: 600, height: 150, style: { width: '100%', height: '150px', cursor: 'crosshair', touchAction: 'none' } }}
                  />
                </Box>
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => sigCanvasRef.current?.clear()}>Clear</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Step 5 — legal */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: compact ? 2 : 3, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={700} gutterBottom>Legal Acknowledgment Required</Typography>
        <FormControlLabel
          control={<Checkbox checked={agreeAuthority} onChange={(e) => setAgreeAuthority(e.target.checked)} size="small" />}
          label={<Typography variant="body2"><strong>APPROVAL AUTHORITY:</strong> I certify I have delegated authority to approve invoices of this type and amount.</Typography>}
          sx={{ mb: 1, alignItems: 'flex-start' }}
        />
        <Divider sx={{ my: 1 }} />
        <FormControlLabel
          control={<Checkbox checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} size="small" />}
          label={<Typography variant="body2"><strong>RECEIVABLE ASSIGNMENT:</strong> Payment MUST be remitted directly to Blitz Systems Corp per the {hasExistingNoa ? 'NOA on file' : 'Notice of Assignment (NOA)'}.</Typography>}
          sx={{ alignItems: 'flex-start' }}
        />
      </Alert>
    </>
  );

  // ── Shared submit footer ─────────────────────────────────────────────── //
  const SubmitFooter = ({ stack = false }) => (
    <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ mb: 1.5 }}>{errorMessage}</Alert>
      )}
      {stack ? (
        <Stack spacing={1}>
          <Button
            fullWidth variant="contained" size="large"
            color={action === 'approve' ? 'success' : 'error'}
            disabled={action === 'approve' ? !isApproveValid || isLoading : isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
            onClick={handleSubmit}
            sx={{ fontWeight: 700, height: 52, borderRadius: 2, textTransform: 'none' }}
          >
            {isLoading ? 'Processing…'
              : action === 'approve'
                ? siblingInvoiceIds?.length ? `Approve All ${siblingInvoiceIds.length + 1} Invoices` : `Approve · ${fmt(invoiceAmount)}`
                : 'Decline Invoice'}
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleClose} disabled={isLoading} sx={{ fontWeight: 600, height: 48, borderRadius: 2, textTransform: 'none' }}>
            Cancel
          </Button>
        </Stack>
      ) : (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" size="large" onClick={handleClose} disabled={isLoading} sx={{ px: 3, borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" size="large"
            color={action === 'approve' ? 'success' : 'error'}
            disabled={action === 'approve' ? !isApproveValid || isLoading : isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
            onClick={handleSubmit}
            sx={{ flex: 1, fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: 2 }}
          >
            {isLoading ? 'Processing…'
              : action === 'approve'
                ? siblingInvoiceIds?.length ? `Approve All ${siblingInvoiceIds.length + 1} Invoices` : 'Approve & Process'
                : 'Decline Invoice'}
          </Button>
        </Box>
      )}
    </Box>
  );

  // ── Mobile ───────────────────────────────────────────────────────────── //
  if (isMobile) {
    return (
      <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { borderRadius: 0 } }}>
        {/* Header */}
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GavelIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                {showNavigation ? `Approval ${currentItemIndex}/${totalItems}` : 'Approval Required'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {showNavigation && (
                <>
                  <IconButton size="small" onClick={onNavigatePrevious} disabled={!onNavigatePrevious}><ChevronLeftIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={onNavigateNext} disabled={!onNavigateNext}><ChevronRightIcon fontSize="small" /></IconButton>
                </>
              )}
              <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
          {currentItemName && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 3 }}>{currentItemName}</Typography>
          )}
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {siblingInvoiceIds?.length > 0 && <Chip label={`Batch · ${siblingInvoiceIds.length + 1}`} color="info" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
            {hasExistingNoa && <Chip icon={<CheckIcon sx={{ fontSize: 12 }} />} label="NOA on File" color="success" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
            {isFirstInvoiceForPayor && !hasExistingNoa && <Chip icon={<WarningIcon sx={{ fontSize: 12 }} />} label="NOA Required" color="warning" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
          </Stack>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, pt: 2, pb: '160px' }}>
          {/* Invoice summary (prop-based, instant) */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Invoice Amount</Typography>
              <Typography variant="h6" fontWeight={800}>{fmt(invoiceAmount)}</Typography>
            </Box>
            {currentItemName && (
              <Typography variant="body2" color="text.secondary" noWrap>{currentItemName}</Typography>
            )}
            <RosterSection />
          </Paper>

          <Alert severity="error" icon={<SecurityIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>⚠️ Immediate Financial Impact</Typography>
            <Typography variant="caption">
              {action === 'approve'
                ? `Approving commits payment of ${fmt(invoiceAmount)}. Cannot be undone.`
                : 'Declining permanently cancels this invoice.'}
            </Typography>
          </Alert>

          {/* Action selection */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>1. Select Action</Typography>
            <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
              <FormControlLabel value="approve" control={<Radio />}
                label={<Box><Typography fontWeight={700} color="success.main">Approve & Process</Typography><Typography variant="caption" color="text.secondary">Authorize payment</Typography></Box>}
                sx={{ mb: 1, p: 1.5, border: action === 'approve' ? '2px solid' : '1px solid', borderColor: action === 'approve' ? 'success.main' : 'grey.300', borderRadius: 2, mr: 0 }}
              />
              <FormControlLabel value="decline" control={<Radio />}
                label={<Box><Typography fontWeight={700} color="error.main">Decline Invoice</Typography><Typography variant="caption" color="text.secondary">Cancel permanently</Typography></Box>}
                sx={{ p: 1.5, border: action === 'decline' ? '2px solid' : '1px solid', borderColor: action === 'decline' ? 'error.main' : 'grey.300', borderRadius: 2, mr: 0 }}
              />
            </RadioGroup>
          </Paper>

          {action === 'approve' && <ApprovalForm compact />}
          {action === 'decline' && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Decline Reason (Optional)</Typography>
              <TextField multiline rows={3} value={reasoning} onChange={(e) => setReasoning(e.target.value)} fullWidth size="small" placeholder="Reason for declining…" />
            </Paper>
          )}
        </Box>

        {/* Fixed footer */}
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, px: 2.5, pt: 2, pb: 3, bgcolor: 'background.paper', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}>
          <SubmitFooter stack />
        </Box>
      </Dialog>
    );
  }

  // ── Desktop ──────────────────────────────────────────────────────────── //
  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'background.default', zIndex: theme.zIndex.modal, display: 'flex', flexDirection: 'column' }}
      onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <Box sx={{ height: 72, px: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <GavelIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              {showNavigation ? `Invoice Approval ${currentItemIndex}/${totalItems}` : 'Invoice Approval Required'}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
              {currentItemName && <Typography variant="body2" color="text.secondary">{currentItemName}</Typography>}
              {siblingInvoiceIds?.length > 0 && <Chip label={`Batch · ${siblingInvoiceIds.length + 1} creators`} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
              {hasExistingNoa && <Chip icon={<CheckIcon />} label="NOA on File" color="success" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
              {isFirstInvoiceForPayor && !hasExistingNoa && <Chip icon={<WarningIcon />} label="NOA Required" color="warning" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
            </Stack>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showNavigation && (
            <>
              <IconButton onClick={onNavigatePrevious} disabled={!onNavigatePrevious}><ChevronLeftIcon /></IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50, textAlign: 'center' }}>{currentItemIndex} / {totalItems}</Typography>
              <IconButton onClick={onNavigateNext} disabled={!onNavigateNext}><ChevronRightIcon /></IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </>
          )}
          {showNavigation && onBackToList && (
            <Button variant="outlined" onClick={onBackToList} sx={{ borderRadius: 2, mr: 1 }}>← Back to List</Button>
          )}
          <IconButton onClick={handleClose} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left — invoice summary from props, instant */}
        <Box sx={{ width: 380, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto', bgcolor: 'background.paper', p: 4 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Invoice Total</Typography>
            <Typography variant="h3" fontWeight={900} sx={{ fontVariantNumeric: 'tabular-nums', mb: 2 }}>
              {fmt(invoiceAmount)}
            </Typography>
            {currentItemName && (
              <Typography variant="body2" color="text.secondary" gutterBottom>{currentItemName}</Typography>
            )}
            <Chip
              label={invoiceType === 'creator' ? 'Creator Invoice' : 'Client Invoice'}
              size="small" color="primary" variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', mt: 1 }}
            />
          </Paper>

          <RosterSection />

          {hasExistingNoa && noaData && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700}>✓ NOA on File</Typography>
              {noaData.noa_sent_to && <Typography variant="caption" display="block">Sent to: {noaData.noa_sent_to}</Typography>}
            </Alert>
          )}
          {isFirstInvoiceForPayor && !hasExistingNoa && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700}>First Invoice</Typography>
              <Typography variant="caption">An NOA will be sent to the AP email after approval.</Typography>
            </Alert>
          )}
        </Box>

        {/* Right — form */}
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 4, flex: 1 }}>
            <Alert severity="error" icon={<SecurityIcon />} sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>⚠️ Immediate Financial Impact</Typography>
              <Typography variant="caption">
                {action === 'approve'
                  ? siblingInvoiceIds?.length
                    ? `Approving processes payment to all ${siblingInvoiceIds.length + 1} creators. Cannot be undone.`
                    : `Approving commits payment of ${fmt(invoiceAmount)}. Cannot be undone.`
                  : 'Declining permanently cancels this invoice.'}
              </Typography>
            </Alert>

            {/* Action selection */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>1. Select Action</Typography>
              <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
                <FormControlLabel value="approve" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="success.main">{siblingInvoiceIds?.length ? `Approve All ${siblingInvoiceIds.length + 1} Invoices` : 'Approve & Process Invoice'}</Typography><Typography variant="caption" color="text.secondary">Authorize payment</Typography></Box>}
                  sx={{ mb: 1.5, p: 2, border: action === 'approve' ? '2px solid' : '1px solid', borderColor: action === 'approve' ? 'success.main' : 'grey.300', borderRadius: 2, mr: 0 }}
                />
                <FormControlLabel value="decline" control={<Radio />}
                  label={<Box><Typography fontWeight={700} color="error.main">Decline Invoice</Typography><Typography variant="caption" color="text.secondary">Permanently cancel</Typography></Box>}
                  sx={{ p: 2, border: action === 'decline' ? '2px solid' : '1px solid', borderColor: action === 'decline' ? 'error.main' : 'grey.300', borderRadius: 2, mr: 0 }}
                />
              </RadioGroup>
            </Paper>

            {action === 'approve' && <ApprovalForm compact={false} />}
            {action === 'decline' && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Decline Reason (Optional)</Typography>
                <TextField multiline rows={4} value={reasoning} onChange={(e) => setReasoning(e.target.value)} fullWidth placeholder="Optionally provide a reason for declining…" />
              </Paper>
            )}
          </Box>

          <Box sx={{ px: 4, pb: 4 }}>
            <SubmitFooter stack={false} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UnifiedInvoiceActionDialog;